import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Paper,
  Backdrop,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { Transaction } from '../../types';

// Create a context for the demo transaction
export const DemoTransactionContext = React.createContext<{
  id: string;
  amount: number;
  description: string;
  category: string;
  date: string;
  type: 'expense' | 'income';
  isDemo: boolean;
} | null>(null);

// Export a hook to access the demo transaction
export const useDemoTransaction = () => React.useContext(DemoTransactionContext);

interface DashboardTourProps {
  children: React.ReactNode;
}

interface TourStep {
  target: string;
  title: React.ReactNode;
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

const useViewportDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 600
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: window.innerWidth < 600
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

// Separate component for the highlight overlay that will be rendered as a portal
const HighlightOverlay: React.FC<{
  highlightRect: DOMRect | null;
  theme: any;
}> = React.memo(({ highlightRect, theme }) => {
  if (!highlightRect) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: highlightRect.top - 16,
        left: highlightRect.left - 16,
        width: highlightRect.width + 32,
        height: highlightRect.height + 32,
        border: `6px solid ${theme.palette.primary.main}`,
        borderRadius: '20px',
        zIndex: 9999996,
        pointerEvents: 'none',
        boxShadow: `
          0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)},
          0 0 30px ${alpha(theme.palette.primary.main, 0.8)}, 
          0 0 60px ${alpha(theme.palette.primary.main, 0.5)},
          inset 0 0 20px ${alpha(theme.palette.primary.main, 0.2)}
        `,
        animation: 'pulse 2s infinite',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    />,
    document.body
  );
});

export const DashboardTour: React.FC<DashboardTourProps> = ({ children }) => {
  const { isTourOpen, closeTour, completeTour } = useOnboarding();
  const theme = useTheme();
  const viewport = useViewportDimensions();
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);

  // Mock transaction for demo purposes
  const mockTransaction = {
    id: 'demo-transaction',
    amount: 45.99,
    description: 'Grocery Shopping',
    category: 'Food & Dining',
    date: new Date().toISOString(),
    type: 'expense' as const,
    isDemo: true // Flag to identify this as a demo transaction
  } as const;

  const steps: TourStep[] = [
    {
      target: '[data-tour="dashboard"]',
      title: 'Welcome to Your Altifi Dashboard!',
      content: (
        <Box textAlign="center">
          <Box sx={{ 
            fontSize: '48px', 
            mb: 2, 
            animation: 'bounce 2s infinite',
            '& span': {
              WebkitTextFillColor: 'initial',
              color: 'initial'
            }
          }}>🎉</Box>
          <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2, color: theme.palette.text.secondary }}>
            Take control of your money with our intuitive budgeting system. 
            This quick tour will show you all the powerful features in just <strong>2 minutes</strong>.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            mt: 2.5, 
            opacity: 0.7,
            '& span': {
              WebkitTextFillColor: 'initial',
              color: 'initial'
            }
          }}>
            <span style={{ fontSize: '24px' }}>💰</span>
            <span style={{ fontSize: '24px' }}>📊</span>
            <span style={{ fontSize: '24px' }}>🎯</span>
            <span style={{ fontSize: '24px' }}>📱</span>
          </Box>
        </Box>
      ),
      placement: 'center'
    },
    {
      target: '[data-tour="month-navigation"]',
      title: (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          '& span': {
            WebkitTextFillColor: 'initial',
            color: 'initial'
          }
        }}>
          <span>📅</span>
          <span>Navigate Through Time</span>
        </Box>
      ),
      content: (
        <Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Easily switch between months to track your financial journey. Use the calendar picker or arrow buttons 
            to navigate through your budget history.
          </Typography>
          <Paper sx={{ 
            background: alpha(theme.palette.info.main, 0.1), 
            p: 1.5, 
            borderRadius: 1, 
            mt: 1.5 
          }}>
            <Typography variant="body2" sx={{ color: theme.palette.info.main, fontWeight: 600 }}>
              💡 Pro Tip: Press <strong>M</strong> to quickly jump to any month!
            </Typography>
          </Paper>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="monthly-summary"]',
      title: (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          '& span': {
            WebkitTextFillColor: 'initial',
            color: 'initial'
          }
        }}>
          <span>📊</span>
          <span>Your Financial Overview</span>
        </Box>
      ),
      content: (
        <Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Get a bird's-eye view of your finances with real-time income, expense, and savings tracking. 
            Watch your progress with beautiful visual indicators.
          </Typography>
          <Paper sx={{ 
            background: alpha(theme.palette.success.main, 0.1), 
            p: 1.5, 
            borderRadius: 1, 
            mt: 1.5 
          }}>
            <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
              ⚡ Quick Action: Use <strong>Balance All</strong> or press <strong>Ctrl+B</strong> to auto-distribute unassigned money!
            </Typography>
          </Paper>
        </Box>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="budget-categories"]',
      title: (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          '& span': {
            WebkitTextFillColor: 'initial',
            color: 'initial'
          }
        }}>
          <span>💰</span>
          <span>Smart Budget Management</span>
        </Box>
      ),
      content: (
        <Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Your money is organized into three powerful categories: Income, Expenses, and Savings. 
            Each category shows progress bars and smart alerts to keep you on track.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, my: 1.5 }}>
            <Paper sx={{ 
              background: alpha(theme.palette.success.main, 0.1), 
              p: 1, 
              borderRadius: 0.75, 
              flex: 1, 
              textAlign: 'center' 
            }}>
              <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                📈 Income
              </Typography>
            </Paper>
            <Paper sx={{ 
              background: alpha(theme.palette.error.main, 0.1), 
              p: 1, 
              borderRadius: 0.75, 
              flex: 1, 
              textAlign: 'center' 
            }}>
              <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                💸 Expenses
              </Typography>
            </Paper>
            <Paper sx={{ 
              background: alpha(theme.palette.info.main, 0.1), 
              p: 1, 
              borderRadius: 0.75, 
              flex: 1, 
              textAlign: 'center' 
            }}>
              <Typography variant="body2" sx={{ color: theme.palette.info.main, fontWeight: 600 }}>
                💎 Savings
              </Typography>
            </Paper>
          </Box>
        </Box>
      ),
      placement: 'right'
    },
    {
      target: '[data-tour="recent-transactions"]',
      title: (
        <>
          <span>📋</span>
          <span>Transaction Central</span>
        </>
      ),
      content: (
        <Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Your financial activity hub! View recent transactions, add new ones, and see everything 
            color-coded by type for instant recognition.
          </Typography>
          <Paper sx={{ 
            background: alpha(theme.palette.error.main, 0.1), 
            p: 1.5, 
            borderRadius: 1, 
            mt: 1.5 
          }}>
            <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
              🔍 Look for: The <strong>+</strong> button to add transactions and <strong>View All</strong> to see your complete history!
            </Typography>
          </Paper>
        </Box>
      ),
      placement: 'left'
    },
    {
      target: '[data-tour="demo-transaction"]',
      title: (
        <>
          <span>✨</span>
          <span>Magic Drag & Drop</span>
        </>
      ),
      content: (
        <Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Here's where the magic happens! Simply grab any transaction and drag it to the right category. 
            Watch as your budget updates in real-time.
          </Typography>
          <Paper sx={{ 
            background: alpha(theme.palette.secondary.main, 0.1), 
            p: 1.5, 
            borderRadius: 1, 
            mt: 1.5,
            borderLeft: `4px solid ${theme.palette.secondary.main}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <span style={{ fontSize: '20px' }}>🎯</span>
              <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                Try it:
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>
              Drag → Drop → Done! It's that simple.
            </Typography>
          </Paper>
        </Box>
      ),
      placement: 'top'
    },
    {
      target: '[data-tour="dashboard"]',
      title: 'Congratulations! You\'re Ready to Budget Like a Pro!',
      content: (
        <Box textAlign="center">
          <Box sx={{ fontSize: '64px', mb: 2.5, animation: 'tada 1s' }}>🚀</Box>
          <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2.5, color: theme.palette.text.secondary }}>
            You now know how to navigate, categorize, and track your finances with ease. 
            Remember these key features to maximize your budgeting success:
          </Typography>
          <Paper sx={{ 
            background: alpha(theme.palette.primary.main, 0.05), 
            p: 2, 
            borderRadius: 1.5, 
            mt: 2.5, 
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: 1.5, 
              textAlign: 'left' 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '18px' }}>🎯</span>
                <Typography variant="body2" sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                  Drag & drop transactions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '18px' }}>⚡</span>
                <Typography variant="body2" sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                  Use Ctrl+B to balance
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '18px' }}>📊</span>
                <Typography variant="body2" sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                  Track progress bars
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '18px' }}>📅</span>
                <Typography variant="body2" sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                  Press M for month picker
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      ),
      placement: 'top'
    }
  ];

  const calculateTooltipPosition = (target: HTMLElement, placement: string) => {
    const rect = target.getBoundingClientRect();
    const tooltipWidth = viewport.isMobile ? viewport.width - 32 : (currentStep === steps.length - 1 ? 520 : 480);
    const tooltipHeight = currentStep === 0 ? 440 : 400; // Welcome step needs more space

    let top = 0;
    let left = 0;

    if (viewport.isMobile) {
      // On mobile, always position at bottom
      top = Math.max(viewport.height - tooltipHeight - 20, 20); // Ensure it doesn't go off-screen
      left = 16;
    } else {
      // Center placement or first step
      if (placement === 'center' || currentStep === 0) {
        // Center the modal in the viewport
        top = Math.max((viewport.height - tooltipHeight) / 2, 20);
        left = (viewport.width - tooltipWidth) / 2;
      } else {
        switch (placement) {
          case 'bottom':
            top = rect.bottom + 20;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'top':
            top = rect.top - tooltipHeight - 20;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'right':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.right + 20;
            break;
          case 'left':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.left - tooltipWidth - 20;
            break;
          default:
            top = rect.bottom + 20;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        }
      }

      // Ensure tooltip stays within viewport with minimum margins
      const minMargin = 20;
      if (left < minMargin) left = minMargin;
      if (left + tooltipWidth > viewport.width - minMargin) left = viewport.width - tooltipWidth - minMargin;
      if (top < minMargin) top = minMargin;
      if (top + tooltipHeight > viewport.height - minMargin) top = viewport.height - tooltipHeight - minMargin;
    }

    return { top, left };
  };

  const highlightElement = (selector: string) => {
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) return;

    // Clear any existing highlight
    if (highlightedElement) {
      highlightedElement.style.boxShadow = '';
      highlightedElement.style.position = '';
      highlightedElement.style.zIndex = '';
      highlightedElement.style.borderRadius = '';
      highlightedElement.style.transition = '';
    }

    // Add highlight styling to the element
    element.style.position = 'relative';
    element.style.zIndex = '9999998';
    element.style.transition = 'all 0.3s ease-in-out';

    // Special handling for the dashboard highlight
    if (selector === '[data-tour="dashboard"]') {
      element.style.borderRadius = '16px';
      element.style.boxShadow = `
        0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)},
        0 0 30px ${alpha(theme.palette.primary.main, 0.2)},
        inset 0 0 30px ${alpha(theme.palette.primary.main, 0.1)}
      `;
    } else {
      element.style.borderRadius = '12px';
      element.style.boxShadow = `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`;
    }
    
    // Update states
    setHighlightedElement(element);
    
    // Get initial position
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setHighlightRect(rect);
      
      // Calculate tooltip position
      const placement = steps[currentStep]?.placement || 'bottom';
      const position = calculateTooltipPosition(element, placement);
      setTooltipPosition(position);
    }

    // Scroll element into view after setting initial position
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: selector === '[data-tour="dashboard"]' ? 'center' : 'nearest',
      inline: 'center'
    });

    // Update position after scroll
    setTimeout(() => {
      const newRect = element.getBoundingClientRect();
      if (newRect.width > 0 && newRect.height > 0) {
        setHighlightRect(newRect);
        
        // Recalculate tooltip position
        const placement = steps[currentStep]?.placement || 'bottom';
        const position = calculateTooltipPosition(element, placement);
        setTooltipPosition(position);
      }
    }, 300);
  };

  // Update highlight position when elements move
  const updateHighlightPosition = () => {
    if (highlightedElement) {
      const rect = highlightedElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setHighlightRect(rect);
        
        // Recalculate tooltip position
        const placement = steps[currentStep]?.placement || 'bottom';
        const position = calculateTooltipPosition(highlightedElement, placement);
        setTooltipPosition(position);
      }
    }
  };

  // Listen for window resize and scroll events
  useEffect(() => {
    if (!highlightedElement) return;

    const handleResize = () => updateHighlightPosition();
    const handleScroll = () => updateHighlightPosition();

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [highlightedElement]);

  // Handle step changes
  useEffect(() => {
    if (isTourOpen && steps[currentStep]) {
      highlightElement(steps[currentStep].target);
    } else if (!isTourOpen) {
      // Cleanup when tour is closed
      if (highlightedElement) {
        highlightedElement.style.boxShadow = '';
        highlightedElement.style.position = '';
        highlightedElement.style.zIndex = '';
        highlightedElement.style.borderRadius = '';
        highlightedElement.style.transition = '';
      }
      setHighlightedElement(null);
      setHighlightRect(null);
    }
  }, [isTourOpen, currentStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    closeTour();
  };

  const handleComplete = () => {
    completeTour();
    setCurrentStep(0);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const currentStepData = steps[currentStep];

  if (!isTourOpen || !currentStepData) return <>{children}</>;

  return (
    <>
      {/* Global animations */}
      <style>
        {`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          
          @keyframes tada {
            0% { transform: scale(1); }
            10%, 20% { transform: scale(0.9) rotate(-3deg); }
            30%, 50%, 70%, 90% { transform: scale(1.1) rotate(3deg); }
            40%, 60%, 80% { transform: scale(1.1) rotate(-3deg); }
            100% { transform: scale(1) rotate(0); }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.02); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>

      {/* Custom overlay with spotlight cutout - rendered as portal to cover full page */}
      {ReactDOM.createPortal(
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999995,
            pointerEvents: 'none',
            background: highlightRect 
              ? `radial-gradient(
                  ellipse ${Math.max(highlightRect.width + 80, 300)}px ${Math.max(highlightRect.height + 80, 200)}px at ${highlightRect.left + highlightRect.width/2}px ${highlightRect.top + highlightRect.height/2}px,
                  transparent 0%,
                  transparent 30%,
                  rgba(0, 0, 0, 0.1) 40%,
                  rgba(0, 0, 0, 0.8) 100%
                )`
              : 'rgba(0, 0, 0, 0.8)',
            transition: 'background 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />,
        document.body
      )}
      
      {/* Highlight overlay rendered as portal */}
      <HighlightOverlay highlightRect={highlightRect} theme={theme} />

      {/* Floating tooltip - rendered as portal to appear above overlay */}
      {ReactDOM.createPortal(
        <Paper
          sx={{
            position: 'fixed',
            top: viewport.isMobile ? 'auto' : `${tooltipPosition.top}px`,
            left: viewport.isMobile ? '16px' : `${tooltipPosition.left}px`,
            right: viewport.isMobile ? '16px' : 'auto',
            bottom: viewport.isMobile ? '20px' : 'auto',
            width: viewport.isMobile ? 'calc(100vw - 32px)' : currentStep === steps.length - 1 ? '520px' : '480px',
            maxWidth: viewport.isMobile ? 'calc(100vw - 32px)' : currentStep === steps.length - 1 ? '520px' : '480px',
            zIndex: 9999999,
            borderRadius: 2,
            overflow: 'hidden', // Ensure content stays within bounds
            backdropFilter: 'blur(20px)',
            background: theme.palette.mode === 'dark' 
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.85)})` 
              : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(theme.palette.background.paper, 0.92)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 20px 40px rgba(0,0,0,0.4), 0 8px 16px rgba(0,0,0,0.2)'
              : '0 20px 40px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.04)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: 0,
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          />

          {/* Content */}
          <Box sx={{ p: 4, pb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: 1.3,
                  flex: 1,
                  pr: 1,
                  '& > span:not(:first-of-type)': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }
                }}
              >
                {currentStepData.title}
              </Typography>
              <IconButton
                onClick={handleSkip}
                size="small"
                sx={{ color: theme.palette.text.secondary }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ minHeight: currentStep === 0 ? 140 : 120 }}>
              {currentStepData.content}
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ 
            px: 4, 
            pb: 3, 
            pt: 1,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            mt: 1
          }}>
            <Button
              onClick={handleSkip}
              size="small"
              sx={{ 
                color: theme.palette.text.secondary,
                textTransform: 'none',
                fontSize: '12px',
              }}
              startIcon={<SkipIcon fontSize="small" />}
            >
              {viewport.isMobile ? 'Skip' : 'Skip Tour'}
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {currentStep > 0 && (
                <Button
                  onClick={handlePrev}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'none' }}
                  startIcon={!viewport.isMobile && <ArrowBackIcon fontSize="small" />}
                >
                  {viewport.isMobile ? 'Back' : 'Previous'}
                </Button>
              )}
              <Button
                onClick={handleNext}
                variant="contained"
                size="small"
                sx={{ 
                  textTransform: 'none',
                  background: currentStep === steps.length - 1 
                    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                    : undefined,
                }}
                endIcon={!viewport.isMobile && currentStep < steps.length - 1 && <ArrowForwardIcon fontSize="small" />}
              >
                {currentStep === steps.length - 1 
                  ? (viewport.isMobile ? 'Start! 🚀' : 'Get Started! 🚀')
                  : (viewport.isMobile ? 'Next' : 'Next →')
                }
              </Button>
            </Box>
          </Box>
        </Paper>,
        document.body
      )}

      {/* Wrap children with mock transaction context */}
      <DemoTransactionContext.Provider value={isTourOpen ? mockTransaction : null}>
        {children}
      </DemoTransactionContext.Provider>
    </>
  );
}; 